import {
  Ban,
  Banknote,
  CheckCircle2,
  FileCheck2,
  History,
  Landmark,
  RotateCcw,
  Send,
  Settings,
  Undo2,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, usePermission } from '../../../auth/AuthContext';
import { PermissionGate } from '../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../components/Breadcrumb';
import { Button } from '../../../components/Button';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ErrorState } from '../../../components/ErrorState';
import { Menu } from '../../../components/Menu';
import { NextActionAlert, type AlertTone } from '../../../components/NextActionAlert';
import { PreviewTable } from '../../../components/PreviewTable';
import { Skeleton } from '../../../components/Skeleton';
import { HistoryLogsModal } from '../../../components/index-page/HistoryLogsModal';
import { ProfileCard } from '../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../components/table/StatusText';
import { ApiError } from '../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../lib/formatDate';
import { formatMoney } from '../../../lib/formatMoney';
import { usePersonNames } from '../../human-resources/personnel/usePersonNames';
import { DisbursementRequestModal } from '../components/DisbursementRequestModal';
import {
  DisbursementItemsPanel,
  DisbursementLifecycleCard,
  disbursementTabs,
  type DisbursementTabKey,
} from '../components/DisbursementRequestTabs';
import {
  ReceiveVoucherModal,
  ReleaseDisbursementModal,
  ReturnDisbursementModal,
} from '../components/DisbursementStepModals';
import { disbursementRequestsApi } from '../disbursementRequestsApi';
import {
  DR_STATUS,
  isReturnable,
  stepPermission,
  type DisbursementRequestDetail,
} from '../disbursementRequestTypes';


/** What is open: a form that COLLECTS, or a dialog that only confirms. One at a time. */
type Pending = 'edit' | 'process' | 'receive' | 'release' | 'return' | 'restore' | null;

/**
 * One request to the Cash Division, on the header layout — it reads as one continuous thing.
 *
 * <b>Every step is the Cash Division's</b>: Accounting is not a user of this system, so a
 * request at Accounting is only tagged, and Cash records the voucher when it comes back. The
 * banner names the next step and offers it to whoever holds that step's key.
 *
 * <b>Sending it for accounting processing is a CONFIRMATION</b> — it collects nothing, so it is a
 * `<ConfirmDialog requireConfirm>`. Receiving the voucher, releasing and returning each COLLECT
 * a value, so each is a form with its own tick and no dialog in front of it.
 */
export function DisbursementRequestProfilePage() {
  const { id } = useParams<{ id: string }>();
  const canReadPersonnel = usePermission('personnel.read');
  const { hasPermission } = useAuth();

  const [request, setRequest] = useState<DisbursementRequestDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<DisbursementTabKey>('info');

  // Both people on the request in ONE resolve — the requester and whoever took the voucher back.
  const requesterPersonId = request?.requesterPersonId;
  const receiverPersonId = request?.voucherReceivedByPersonId;
  const personIds = useMemo(
    () => [requesterPersonId, receiverPersonId].filter((p): p is string => p != null),
    [requesterPersonId, receiverPersonId],
  );
  const names = usePersonNames(canReadPersonnel ? personIds : []);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      disbursementRequestsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setRequest(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this request.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Cash Division',
    { label: 'Disbursements', to: '/cash-division/requests' },
    request ? request.requestNumber : 'Request',
  ];

  // Guarded on having NOTHING to show — a failed refresh keeps the record and goes to the banner.
  if (error && !request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const record = request;
  const requesterName = !canReadPersonnel
    ? 'Hidden'
    : record.requesterPersonId == null
      ? 'Restricted'
      : names.statusOf(record.requesterPersonId) === 'erased'
        ? 'Identity erased'
        : (names.nameOf(record.requesterPersonId) ?? (names.failed ? 'Name unavailable' : null));

  const receivedByName =
    canReadPersonnel && record.voucherReceivedByPersonId != null
      ? (names.nameOf(record.voucherReceivedByPersonId) ?? null)
      : null;

  const editDisabledReason = record.isEditable
    ? null
    : !record.isActive
      ? 'This request is deleted. Restore it before correcting it.'
      : 'Only a request still waiting on the Cash Division can be corrected.';

  function saved(next: DisbursementRequestDetail) {
    setRequest(next);
    setPending(null);
  }

  async function runConfirmed() {
    setActing(true);
    setActionError(null);

    try {
      if (pending === 'process') {
        saved(await disbursementRequestsApi.process(record.id));
      } else if (pending === 'restore') {
        await disbursementRequestsApi.restore(record.id);
        setPending(null);
        load();
      }
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setActing(false);
    }
  }

  const banner = bannerFor(record);
  const step = stepPermission(record.status);

  const actions = [
    {
      /*
        RETURNING is in the header cog rather than beside the banner's primary step: it is the
        exception, not the next thing to do. It carries the key of the step the request is
        standing on — the server checks the same — and is DISABLED for state once the voucher
        is back, because a payment waiting to go out is corrected by a delete, not a return.
      */
      key: 'return',
      label: 'Return request',
      icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
      permission: step ?? 'disbursement-requests.process',
      disabledReason: !record.isActive
        ? 'This request is deleted. Restore it before acting on it.'
        : isReturnable(record.status)
          ? null
          : 'Only a request still with the Cash Division or at Accounting can be returned.',
      onClick: () => setPending('return'),
    },
    {
      key: 'history',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setHistoryOpen(true),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="disbursement-request-profile"
        header={
          <ProfileHeader
            id="disbursement-request-profile-header"
            icon={<Wallet className="h-6 w-6" aria-hidden="true" />}
            title={record.requestNumber}
            meta={[record.transactionTypeName, record.paymentTypeName, formatMoney(record.amount)]}
            secondary={[requesterName ? `Requested for ${requesterName}` : null]}
            status={
              <span className="flex items-center gap-2">
                <StatusText tone="inactive">{record.statusLabel}</StatusText>
                <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                  {record.isActive ? 'Active' : 'Deleted'}
                </StatusText>
              </span>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            /*
              NO COG where every entry is gated away — `<Menu>` draws its trigger regardless, so a
              read-only reader would meet a cog that opens an empty panel.
            */
            actions={
              actions.some((a) => hasPermission(a.permission)) ? (
              <Menu
                id="disbursement-request-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Request settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
              ) : null
            }
          />
        }
        banner={
          <NextActionAlert
            id="disbursement-request-banner"
            icon={<banner.icon className="h-4 w-4" aria-hidden="true" />}
            title={error ? 'This page may be out of date' : banner.title}
            description={error ? error.message : banner.description}
            tone={error ? 'red' : banner.tone}
            action={
              <BannerAction
                record={record}
                disabled={acting}
                onProcess={() => setPending('process')}
                onReceive={() => setPending('receive')}
                onRelease={() => setPending('release')}
                onRestore={() => setPending('restore')}
              />
            }
          />
        }
        // The Items tab owns its own chrome and runs edge to edge — the purchase request's layout.
        panel={tab === 'items' ? 'bleed' : 'cards'}
        tabs={disbursementTabs(record)}
        activeTab={tab}
        onTabChange={(next) => setTab(next as DisbursementTabKey)}
      >
        {tab === 'items' ? (
          <DisbursementItemsPanel id="disbursement-request-profile-items" record={record} />
        ) : tab === 'lifecycle' ? (
          <DisbursementLifecycleCard
            id="disbursement-request-profile-lifecycle"
            record={record}
            receivedByName={receivedByName}
          />
        ) : (
          <>
            <ProfileCard
              id="disbursement-request-profile-request"
              title="Request"
              onEdit={() => setPending('edit')}
              editPermission="disbursement-requests.update"
              editDisabledReason={editDisabledReason}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Request number', value: record.requestNumber, mono: true },
                  { label: 'Requesting party', value: requesterName },
                  { label: 'Transaction type', value: record.transactionTypeName },
                  { label: 'Payment type', value: record.paymentTypeName },
                  { label: 'Total amount', value: formatMoney(record.amount), mono: true },
                  { label: 'Remarks', value: record.notes },
                  { label: 'Voucher number', value: record.voucherNumber, mono: true },
                  {
                    label: 'Voucher date',
                    value: record.voucherDate != null ? formatDateOnly(record.voucherDate) : null,
                    mono: true,
                  },
                  { label: 'Received by', value: receivedByName },
                  { label: 'Payment reference', value: record.disbursementReference, mono: true },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="disbursement-request-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt != null ? (
                        <time dateTime={toIso(record.updatedAt)} className="font-mono">
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {pending === 'edit' ? (
        <DisbursementRequestModal open editing={record} onClose={() => setPending(null)} onSaved={saved} />
      ) : null}

      {pending === 'receive' ? (
        <ReceiveVoucherModal request={record} onClose={() => setPending(null)} onSaved={saved} />
      ) : null}

      {pending === 'release' ? (
        <ReleaseDisbursementModal request={record} onClose={() => setPending(null)} onSaved={saved} />
      ) : null}

      {pending === 'return' ? (
        <ReturnDisbursementModal
          request={record}
          atAccounting={record.status === DR_STATUS.atAccounting}
          onClose={() => setPending(null)}
          onSaved={saved}
        />
      ) : null}

      {pending === 'process' || pending === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={pending === 'process' ? 'For Accounting Processing' : 'Restore request'}
          requireConfirm
          loading={acting}
          id={`disbursement-request-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {pending === 'process' ? (
                <>
                  <p>
                    {record.requestNumber} is marked <strong>validated</strong> and sent for accounting
                    processing — tagged <strong>At Accounting</strong>. Its details can no longer be
                    corrected.
                  </p>
                  <p>
                    Accounting works outside this system: when it sends the voucher back, record it
                    here with <strong>Receive voucher</strong>.
                  </p>
                </>
              ) : (
                <p>{record.requestNumber} returns to the register where it was in the route.</p>
              )}

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="disbursement-requests"
        entityId={record.id}
      />
    </div>
  );
}

/** The banner's one primary step, gated on that step's own key. Hidden, never disabled, without it. */
function BannerAction({
  record,
  disabled,
  onProcess,
  onReceive,
  onRelease,
  onRestore,
}: {
  record: DisbursementRequestDetail;
  disabled: boolean;
  onProcess: () => void;
  onReceive: () => void;
  onRelease: () => void;
  onRestore: () => void;
}) {
  if (!record.isActive) {
    return (
      <PermissionGate permission="disbursement-requests.delete">
        <Button
          id="disbursement-request-restore"
          variant="secondary"
          leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
          onClick={onRestore}
          disabled={disabled}
        >
          Restore
        </Button>
      </PermissionGate>
    );
  }

  switch (record.status) {
    case DR_STATUS.cashProcessing:
      return (
        <PermissionGate permission="disbursement-requests.process">
          <Button
            id="disbursement-request-process"
            variant="primary"
            leading={<Send className="h-4 w-4" aria-hidden="true" />}
            onClick={onProcess}
            disabled={disabled}
          >
            For Accounting Processing
          </Button>
        </PermissionGate>
      );
    case DR_STATUS.atAccounting:
      return (
        <PermissionGate permission="disbursement-requests.receive-voucher">
          <Button
            id="disbursement-request-receive-voucher"
            variant="primary"
            leading={<FileCheck2 className="h-4 w-4" aria-hidden="true" />}
            onClick={onReceive}
            disabled={disabled}
          >
            Receive voucher
          </Button>
        </PermissionGate>
      );
    case DR_STATUS.forRelease:
      return (
        <PermissionGate permission="disbursement-requests.release">
          <Button
            id="disbursement-request-release"
            variant="primary"
            leading={<Banknote className="h-4 w-4" aria-hidden="true" />}
            onClick={onRelease}
            disabled={disabled}
          >
            Release
          </Button>
        </PermissionGate>
      );
    default:
      return null;
  }
}

/** One banner for every state, not only the actionable ones. */
function bannerFor(record: DisbursementRequestDetail): {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
  title: string;
  description: string;
  tone: AlertTone;
} {
  if (!record.isActive) {
    return {
      icon: Ban,
      title: 'Deleted',
      description: 'This request is hidden from the register. Restore it to put it back where it was in the route.',
      tone: 'slate',
    };
  }

  switch (record.status) {
    case DR_STATUS.cashProcessing:
      return {
        icon: Wallet,
        title: 'Waiting on the Cash Division',
        description: 'Validate the details, then send it for accounting processing. It can still be corrected until then.',
        tone: 'amber',
      };
    case DR_STATUS.atAccounting:
      return {
        icon: Landmark,
        title: 'At Accounting',
        description:
          'Accounting is processing it and will issue the voucher outside this system. Record the voucher here when it comes back.',
        tone: 'blue',
      };
    case DR_STATUS.forRelease:
      return {
        icon: FileCheck2,
        title: 'For release',
        description: `Voucher ${record.voucherNumber ?? ''} is back from Accounting. Release the payment to the requesting party.`,
        tone: 'amber',
      };
    case DR_STATUS.disbursed:
      return {
        icon: CheckCircle2,
        title: 'Disbursed',
        description: `Paid out on ${record.disbursedOn != null ? formatDateOnly(record.disbursedOn) : '—'} under ${record.disbursementReference ?? '—'}.`,
        tone: 'emerald',
      };
    case DR_STATUS.returned:
      return {
        icon: Undo2,
        title: 'Returned',
        description: `Returned${record.returnedFromStatusLabel != null ? ` from ${record.returnedFromStatusLabel}` : ''}: ${record.returnReason ?? ''}`,
        tone: 'red',
      };
    default:
      return { icon: Wallet, title: record.statusLabel, description: '', tone: 'slate' };
  }
}
